import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import NotFound from "pages/NotFound";
import AdminDashboard from './pages/admin-dashboard';
import AdminBusinessManagement from './pages/admin-business';
import AdminOverview from './pages/admin-overview';
import AdminComplaints from './pages/admin-complaints';
import AdminUsers from './pages/admin-users';
import AdminMonitoring from './pages/admin-monitoring';
import ClientDashboard from './pages/client-dashboard';
import ReportViewer from './pages/report-viewer';
import ExpertMarketplaceListing from './pages/expert-marketplace-listing';
import ExpertDashboard from './pages/expert-dashboard';
import LandingPage from './pages/landing-page';
import NewCaseCreationWizard from './pages/new-case-creation-wizard';
import BlogListingPage from './pages/blog-listing-page';
import IndividualBlogPostPage from './pages/individual-blog-post-page';
import DebugProfile from './pages/debug-profile';
import LegalPagesHub from './pages/legal-pages-hub';
import AboutPage from './pages/about';
import ContactPage from './pages/contact';
import ClarifyingQuestionsPage from './pages/clarifying-questions';
import Login from './pages/login';
import SignUp from './pages/signup';
import VerifyEmailRequired from './pages/verify-email-required';
import TwoFASetup from './pages/2fa-setup';
import TwoFASetupRequired from './pages/2fa-setup-required';
import Unauthorized from './pages/unauthorized';
import EmailPreferences from './pages/email-preferences';
import EnterpriseSalesPage from './pages/enterprise-sales';
import TermsOfServicePage from './pages/terms-of-service';
import PrivacyCenter from './pages/privacy-center';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import ReportDisclaimerPage from './pages/report-disclaimer';
import PrivacyPolicyPage from './pages/privacy-policy';
import RefundPolicyPage from './pages/refund-policy';
import ReportsList from './pages/reports-list';
import ClientMessages from './pages/client-dashboard/messages';
import ClientActivity from './pages/client-dashboard/activity';
import ClientFinancial from './pages/client-dashboard/financial';
import AccountPage from './pages/account';


const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing-page" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email-required" element={<VerifyEmailRequired />} />
        {/* H-07: require authentication but NOT 2FA (user is here to set it up) */}
        <Route
          path="/2fa-setup"
          element={
            <ProtectedRoute require2FA={false}>
              <TwoFASetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/2fa-setup-required"
          element={
            <ProtectedRoute require2FA={false}>
              <TwoFASetupRequired />
            </ProtectedRoute>
          }
        />
        <Route path="/unauthorized" element={<Unauthorized />} />
        
        {/* Blog Routes - Public */}
        <Route path="/blog" element={<BlogListingPage />} />
        <Route path="/blog-listing-page" element={<BlogListingPage />} />
        <Route path="/blog/:slug" element={<IndividualBlogPostPage />} />
        <Route path="/individual-blog-post-page" element={<IndividualBlogPostPage />} />
        
        {/* Legal Routes - Public */}
        <Route path="/legal-pages-hub" element={<LegalPagesHub />} />
        <Route path="/terms" element={<LegalPagesHub />} />
        <Route path="/privacy" element={<LegalPagesHub />} />
        <Route path="/refunds" element={<LegalPagesHub />} />
        
        {/* Company Routes - Public */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Debug Route - Protected */}
        <Route
          path="/debug-profile"
          element={
            <ProtectedRoute>
              <DebugProfile />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes - Client */}
        <Route
          path="/client-dashboard"
          element={
            <ProtectedRoute allowedRoles={['client', 'admin']}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-dashboard/messages"
          element={
            <ProtectedRoute allowedRoles={['client', 'admin']}>
              <ClientMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-dashboard/activity"
          element={
            <ProtectedRoute allowedRoles={['client', 'admin']}>
              <ClientActivity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-dashboard/financial"
          element={
            <ProtectedRoute allowedRoles={['client', 'admin']}>
              <ClientFinancial />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-case-creation-wizard"
          element={
            <ProtectedRoute allowedRoles={['client', 'admin']}>
              <NewCaseCreationWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report-viewer"
          element={
            <ProtectedRoute allowedRoles={['client', 'expert', 'admin']}>
              <ReportsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report-viewer/:caseId"
          element={
            <ProtectedRoute allowedRoles={['client', 'expert', 'admin']}>
              <ReportViewer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clarifying-questions/:caseId"
          element={
            <ProtectedRoute allowedRoles={['client', 'admin']}>
              <ClarifyingQuestionsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes - Expert Marketplace */}
        <Route
          path="/expert-marketplace-listing"
          element={
            <ProtectedRoute allowedRoles={['client', 'expert', 'admin']}>
              <ExpertMarketplaceListing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expert-dashboard/*"
          element={
            <ProtectedRoute allowedRoles={['expert', 'admin']}>
              <ExpertDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes - Admin Only */}
        <Route
          path="/admin-overview"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-monitoring"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminMonitoring />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-complaints"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminComplaints />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-business"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBusinessManagement />
            </ProtectedRoute>
          }
        />
        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        <Route path="/email-preferences" element={<ProtectedRoute><EmailPreferences /></ProtectedRoute>} />
        <Route path="/enterprise-sales" element={<EnterpriseSalesPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/report-disclaimer" element={<ReportDisclaimerPage />} />
        <Route path="/privacy-center" element={<ProtectedRoute><PrivacyCenter /></ProtectedRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />

        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
